/**
 * Browser helper: fire-and-forget feedback votes for the Chat workspace.
 * Does not throw; never blocks the UI.
 */

import type {
  ProductFeedbackAction,
  ProductFeedbackSource,
  ProductFeedbackTool,
} from "@/lib/product-feedback";

export type SubmitCareFeedbackInput = {
  source: Exclude<ProductFeedbackSource, "contact" | "suggestions">;
  action: Exclude<ProductFeedbackAction, "submit">;
  tool?: ProductFeedbackTool | null;
  locale?: string;
  /** Optional free-text reason (e.g. usefulness “no”). Max applied server-side. */
  message?: string | null;
  /** Optional structured reason code (e.g. quality, unchanged). */
  reasonCode?: string | null;
};

export function submitCareFeedback(input: SubmitCareFeedbackInput): void {
  if (typeof window === "undefined") return;
  try {
    const path = window.location.pathname.slice(0, 200);
    const rawLang =
      input.locale ??
      (typeof document !== "undefined" ? document.documentElement.lang : undefined) ??
      "en";
    // Schema requires 2–10 chars; bare "" / "e" from partial lang tags would 400.
    const locale =
      typeof rawLang === "string" && rawLang.trim().length >= 2
        ? rawLang.trim().slice(0, 10)
        : "en";

    const message =
      typeof input.message === "string" && input.message.trim()
        ? input.message.trim().slice(0, 500)
        : null;
    const reasonCode =
      typeof input.reasonCode === "string" && input.reasonCode.trim()
        ? input.reasonCode.trim().slice(0, 40)
        : null;

    const payload = {
      source: input.source,
      action: input.action,
      tool: input.tool ?? null,
      locale,
      path,
      ...(message ? { message } : {}),
      ...(reasonCode ? { reasonCode } : {}),
    };
    const body = JSON.stringify(payload);
    // Guard: never POST non-JSON / empty (avoids Discord noise + 400 parse spam).
    /* v8 ignore next -- residual branch: JSON.stringify of an object always starts with "{" */
    if (!body || body[0] !== "{") return;

    void fetch("/api/care-feedback", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      credentials: "same-origin",
      // keepalive helps unload beacons; some browsers send truncated bodies —
      // we still prefer it for dismiss-on-navigate, and server soft-fails parse errors.
      keepalive: true,
      body,
    }).catch(() => {
      // ignore network errors
    });
  } catch {
    // ignore
  }
}
